import React from 'react';

import { METADATA_TYPES } from '../types/MetadataType';
import type { MetadataOptionType, MetadataSettableType } from '../types/MetadataSettableType';
import type { TenantProfileType } from '../types/TenantProfileType';
import type { RequirementIdsType } from './filter-market-entities';
import {
    FAIRTRANSPORT_MEMBERSHIP_TYPES,
    FairtransportMembershipType
} from '../types/FairtransportMembershipType';

export const defaultMissingRequirements = {
    isMissingRequirements: false,
    missingVehicleTypes: [],
    missingEquipment: [],
    missingSustainability: [],
    missingLicencesCertificates: [],
    missingCustomLicencesCertificates: [],
    missingFairTransport: false
};
type MetadataArrayFields = {
    vehicleTypes?: string[];
    equipment?: string[];
    sustainability?: string[];
    licencesCertificates?: string[];
    customLicencesCertificates?: string[];
};

type CleanedMetadataFields = {
    vehicles: string[];
    equipment: string[];
    sustainability: string[];
    licencesCertificates: string[];
    customLicencesCertificates: string[];
};

type OnChangeType = (e: {
    [key: string]: any;
}) => void;

type EntityType = Record<string, any>;

type SetCustomLicencesCertificatesType = React.Dispatch<React.SetStateAction<{
    id: string;
    value: string;
}[]>>

type CustomLicencesCertificatesType = {
    id: string;
    value: string;
}[];

const parseArrary = (arr?: string[]) => (arr || []).filter((item: string) => item && item.trim());

export const cleanEntityMetadata = <T extends Record<string, any>>(
    entity: T & MetadataArrayFields
): T & CleanedMetadataFields => {
    const normalizedLicCertsForComparison = parseArrary(entity.licencesCertificates)
        .map((cert: string) => cert.toLowerCase());
    const filteredCustomLicCerts = parseArrary(entity.customLicencesCertificates)
        .filter((cert: string) => !normalizedLicCertsForComparison.includes(cert.toLowerCase().trim()));
    const uniqueCustomLicCerts = Array.from(new Set(filteredCustomLicCerts));

    return {
        ...entity,
        vehicles: parseArrary(entity.vehicleTypes),
        equipment: parseArrary(entity.equipment),
        sustainability: parseArrary(entity.sustainability),
        licencesCertificates: parseArrary(entity.licencesCertificates),
        customLicencesCertificates: uniqueCustomLicCerts
    };
};

export const hasStandardProperty = (
    { array, value }
        : { array: string[], value: string }
) => {
    return array && array.includes(value);
};

export const toggleStandardProperty = ({
    key,
    array,
    value,
    onChange,
    entity
}: {
    key: string,
    array: string[],
    value: string,
    onChange: OnChangeType,
    entity: EntityType
}) => {
    const createUpdatedArray = () => {
        if (array && array.includes(value)) {
            return array.filter((p) => p !== value);
        }
        return [...(array || []), value];
    };
    onChange({
        ...entity,
        [key]: createUpdatedArray()
    });
};

export const removeCustomLicenceCertifiacate = ({
    certId,
    customLicencesCertificates,
    setCustomLicencesCertificates,
    entity,
    onChange
}: {
    certId: string,
    customLicencesCertificates: CustomLicencesCertificatesType,
    setCustomLicencesCertificates: SetCustomLicencesCertificatesType,
    entity: EntityType,
    onChange: OnChangeType
}) => {
    const item = customLicencesCertificates.find((i) => i.id === certId);
    const newItems = customLicencesCertificates.filter((i) => i.id !== certId);

    setCustomLicencesCertificates(newItems);
    if ((item as any)?.value && (entity.customLicencesCertificates || []).includes((item as any).value)) {
        onChange({
            ...entity,
            [METADATA_TYPES.CUSTOM_LICENCES_CERTIFICATES]:
                (entity.customLicencesCertificates || []).filter((p: string) => p !== (item as any).value)
        });
    }
};

export const changeCustomLicenceCertificate = ({
    certId,
    newValue,
    customLicencesCertificates,
    setCustomLicencesCertificates,
    entity,
    onChange,
}: {
    certId: string;
    newValue: string,
    customLicencesCertificates: CustomLicencesCertificatesType,
    setCustomLicencesCertificates: SetCustomLicencesCertificatesType,
    entity: EntityType,
    onChange: OnChangeType
}) => {
    const item = customLicencesCertificates.find((i) => i.id === certId);
    const oldValue = (item as any)?.value;
    // eslint-disable-next-line no-confusing-arrow
    const newItems = customLicencesCertificates.map((i) => i.id === certId ? { ...i, value: newValue } : i);
    setCustomLicencesCertificates(newItems);

    const updatedValues = [...(entity.customLicencesCertificates || [])];

    if (oldValue) {
        const oldIndex = updatedValues.indexOf(oldValue);
        if (oldIndex > -1) {
            updatedValues.splice(oldIndex, 1);
        }
    }

    if (newValue.trim()) {
        updatedValues.push(newValue);
    }

    onChange({
        ...entity,
        [METADATA_TYPES.CUSTOM_LICENCES_CERTIFICATES]: updatedValues
    });
};

export const isCustomCertAlreadyInList = ({
    certId,
    value,
    certs
}: {
    certId: string,
    value: string
    certs: CustomLicencesCertificatesType
}) => {
    const firstMatch = certs.find(
        (c) => c.value.toLowerCase().trim() === value.toLowerCase().trim()
    );

    return !!firstMatch && firstMatch.id !== certId;
};

export const isCustomCertInPresets = ({
    value,
    presets
}: {
    value: string,
    presets: string[]
}) => {
    if (!value.trim()) {
        return false;
    }
    return presets.some((cert: string) => cert.toLowerCase() === value.toLowerCase().trim());
};

export const isTenantFairtransportMember = (fairtransportMembership: FairtransportMembershipType) => {
    const membershipValues = Object.values(FAIRTRANSPORT_MEMBERSHIP_TYPES);
    if (!fairtransportMembership) {
        return false;
    }
    return membershipValues.includes(fairtransportMembership);
};

export const getFairtransportValueLevel = (fairtransportMembership: FairtransportMembershipType): number => {
    switch (fairtransportMembership) {
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.VALUELEVEL1:
            return 1;
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.VALUELEVEL2:
            return 2;
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.VALUELEVEL3:
            return 3;
        default:
            return 0;
    }
};

export const getFairtransportTitle = (
    { isPublic, valueLevel }
    : { isPublic: boolean | undefined, valueLevel: number }
): string => {
    return `${isPublic
        ? 'Detta'
        : 'Ert'
    } företag är certifierat enligt Fair Transports kriterier för hållbarhet och socialt ansvar inom transportsektorn
    ${valueLevel > 0
        ? ` på mervärdesnivå ${valueLevel}`
        : ''}`;
};

/** The profile's requirement names mapped to option ids, for comparing with the market annotations. */
export function getProfileRequirementIds(
    profile: Pick<TenantProfileType, 'vehicleTypes' | 'equipment' | 'licencesCertificates' | 'sustainability' | 'fairtransportMembership'>,
    settable: MetadataSettableType
): RequirementIdsType {
    const toIds = (names: string[] = [], options: MetadataOptionType[] = []) => options
        .filter((o) => names.includes(o.name))
        .map((o) => o.id);

    const sustainabilityIds = toIds(profile.sustainability, settable.sustainabilityOptions);
    const fairTransport = (settable.sustainabilityOptions ?? []).find((o) => o.name === 'Fair Transport');
    // Any non-empty membership counts, as in the API
    if (fairTransport && profile.fairtransportMembership && !sustainabilityIds.includes(fairTransport.id)) {
        sustainabilityIds.push(fairTransport.id);
    }

    return {
        vehicleTypeIds: toIds(profile.vehicleTypes, settable.vehicleTypeOptions),
        equipmentIds: toIds(profile.equipment, settable.equipmentOptions),
        licenceCertificateIds: toIds(profile.licencesCertificates, settable.licenceCertificateOptions),
        sustainabilityIds
    };
}

/** The market's default requirement filter: carriers only, null until profile and options are loaded. */
export function getDefaultFulfilsProfile(
    profile: (Parameters<typeof getProfileRequirementIds>[0] & { isCarrier?: boolean }) | null | undefined,
    settable: Parameters<typeof getProfileRequirementIds>[1]
): RequirementIdsType | null {
    return profile?.isCarrier && settable?.vehicleTypeOptions ? getProfileRequirementIds(profile, settable) : null;
}
